import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const VOICE_OPTIONS = [
  { id: 'alloy', label: 'Alloy', description: 'Neutral, balanced' },
  { id: 'echo', label: 'Echo', description: 'Warm, conversational' },
  { id: 'fable', label: 'Fable', description: 'Expressive, narrative' },
  { id: 'onyx', label: 'Onyx', description: 'Deep, authoritative' },
  { id: 'nova', label: 'Nova', description: 'Energetic, friendly' },
  { id: 'shimmer', label: 'Shimmer', description: 'Clear, optimistic' }
];

const COMPONENT_COLORS = {
  'hook': 'bg-purple-100 text-purple-800',
  'main message': 'bg-blue-100 text-blue-800',
  'cta': 'bg-green-100 text-green-800',
  'full script': 'bg-orange-100 text-orange-800',
  'message + cta': 'bg-teal-100 text-teal-800',
  'subject line': 'bg-red-100 text-red-800',
  'preheader': 'bg-pink-100 text-pink-800',
  'header': 'bg-indigo-100 text-indigo-800',
  'body copy': 'bg-yellow-100 text-yellow-800',
  'cta button': 'bg-green-100 text-green-800',
  'footer': 'bg-gray-100 text-gray-800',
  'headline': 'bg-blue-100 text-blue-800',
  'subheadline': 'bg-sky-100 text-sky-800',
  'value proposition': 'bg-violet-100 text-violet-800',
  'feature bullets': 'bg-emerald-100 text-emerald-800',
};

const getComponentColor = (component) => {
  const key = (component || '').toLowerCase();
  return COMPONENT_COLORS[key] || 'bg-gray-100 text-gray-700';
};

const isVoiceEligible = (card) => {
  const ct = card.marketingCopy?.copyType;
  const comp = (card.marketingCopy?.component || '').toLowerCase();
  return (ct === '30s' || ct === '15s') && comp === 'full script';
};

const MarketingCard = ({ card, token, onUpdate, onExecuteProposal }) => {
  const [editText, setEditText] = useState(card.description || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Tweak chat state
  const [showTweak, setShowTweak] = useState(false);
  const [tweakInput, setTweakInput] = useState('');
  const [tweakHistory, setTweakHistory] = useState([]);
  const [tweakLoading, setTweakLoading] = useState(false);
  const [tweakProposal, setTweakProposal] = useState(null);

  // Voice state
  const [selectedVoice, setSelectedVoice] = useState('nova');
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(card.marketingCopy?.voiceAudio?.path ? `/${card.marketingCopy.voiceAudio.path}` : null);
  const audioRef = useRef(null);

  useEffect(() => {
    setEditText(card.description || '');
    if (card.marketingCopy?.voiceAudio?.path) {
      const p = card.marketingCopy.voiceAudio.path;
      setAudioUrl(card.marketingCopy.voiceAudio.storage === 's3' ? p : `/${p}`);
      setSelectedVoice(card.marketingCopy.voiceAudio.voice || 'nova');
    }
  }, [card]);

  const saveText = async () => {
    if (editText === card.description) return;
    setSaving(true);
    try {
      const res = await axios.put(`/api/cards/${card._id}`, { description: editText }, {
        headers: { 'x-auth-token': token }
      });
      onUpdate && onUpdate(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const sendTweak = async () => {
    if (!tweakInput.trim()) return;
    setTweakLoading(true);
    const userMsg = tweakInput;
    setTweakInput('');
    const newHistory = [...tweakHistory, { role: 'user', content: userMsg }];
    setTweakHistory(newHistory);

    try {
      const res = await axios.post('/api/marketing/chat', {
        message: userMsg,
        history: tweakHistory,
        mode: 'tweak',
        cardId: card._id,
        cardTitle: card.title,
        cardDescription: editText,
        component: card.marketingCopy?.component,
        boardId: card.board
      }, { headers: { 'x-auth-token': token } });

      const data = res.data;
      setTweakHistory([...newHistory, { role: 'assistant', content: data.text }]);

      if (data.type === 'proposal' && data.proposals?.length > 0) {
        setTweakProposal(data);
      }
    } catch (err) {
      console.error('Tweak error:', err);
      setTweakHistory([...newHistory, { role: 'assistant', content: 'Sorry, something went wrong.' }]);
    } finally {
      setTweakLoading(false);
    }
  };

  const applyTweak = async () => {
    if (!tweakProposal) return;
    await onExecuteProposal(tweakProposal.proposals);
    setTweakProposal(null);
    setShowTweak(false);
    setTweakHistory([]);
  };

  const generateVoice = async () => {
    if (!editText.trim()) return;
    setVoiceLoading(true);
    try {
      const res = await axios.post('/api/marketing/voice', {
        cardId: card._id,
        text: editText,
        voice: selectedVoice
      }, { headers: { 'x-auth-token': token } });
      setAudioUrl(res.data.url);
    } catch (err) {
      console.error('Voice error:', err);
      alert('Voice generation failed: ' + (err.response?.data?.msg || err.message));
    } finally {
      setVoiceLoading(false);
    }
  };

  const component = card.marketingCopy?.component || '';
  const badgeClass = getComponentColor(component);
  const showVoice = isVoiceEligible(card);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            {component && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${badgeClass}`}>
                {component}
              </span>
            )}
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{card.title}</h3>
          </div>
          <button
            onClick={() => setShowTweak(!showTweak)}
            className="text-xs px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 text-gray-500 dark:text-gray-400 whitespace-nowrap flex-shrink-0 transition-colors"
            title="Tweak with AI"
          >
            ✏️ Tweak
          </button>
        </div>

        <textarea
          className="w-full text-sm text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 min-h-[80px]"
          value={editText}
          onChange={e => setEditText(e.target.value)}
          onBlur={saveText}
          placeholder="Copy content..."
          rows={4}
        />

        <div className="flex justify-end mt-1">
          {saving && <span className="text-xs text-gray-400 dark:text-gray-500">Saving...</span>}
          {saved && <span className="text-xs text-green-500">Saved</span>}
        </div>

        {/* Voice Generation Section */}
        {showVoice && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Voice Generation</p>
            <div className="flex gap-2 items-center mb-2 flex-wrap">
              <select
                className="text-xs border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300"
                value={selectedVoice}
                onChange={e => setSelectedVoice(e.target.value)}
              >
                {VOICE_OPTIONS.map(v => (
                  <option key={v.id} value={v.id}>{v.label} — {v.description}</option>
                ))}
              </select>
              <button
                onClick={generateVoice}
                disabled={voiceLoading || !editText.trim()}
                className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {voiceLoading ? '⏳ Generating...' : audioUrl ? '🔄 Regenerate' : '🎙 Generate Voice'}
              </button>
            </div>
            {audioUrl && (
              <audio
                ref={audioRef}
                src={audioUrl}
                controls
                className="w-full mt-1"
                style={{ height: '36px' }}
              />
            )}
          </div>
        )}
      </div>

      {/* Tweak Chat Panel */}
      {showTweak && (
        <div className="border-t border-blue-100 dark:border-gray-700 bg-blue-50 dark:bg-gray-700 p-3">
          <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2">AI Tweak</p>

          {tweakHistory.length > 0 && (
            <div className="space-y-2 mb-2 max-h-40 overflow-y-auto">
              {tweakHistory.map((msg, i) => (
                <div key={i} className={`text-xs px-2 py-1.5 rounded-lg ${msg.role === 'user' ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 ml-4' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 mr-4 border border-gray-200 dark:border-gray-600'}`}>
                  {msg.content}
                </div>
              ))}
            </div>
          )}

          {tweakProposal && (
            <div className="bg-white dark:bg-gray-800 border border-blue-300 dark:border-gray-600 rounded-lg p-2 mb-2">
              <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">{tweakProposal.text}</p>
              {tweakProposal.proposals?.[0]?.data?.description && (
                <pre className="text-xs bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-200 p-2 rounded whitespace-pre-wrap mb-2 max-h-24 overflow-y-auto">
                  {tweakProposal.proposals[0].data.description}
                </pre>
              )}
              <div className="flex gap-2">
                <button onClick={applyTweak} className="text-xs bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700">
                  Apply
                </button>
                <button onClick={() => setTweakProposal(null)} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                  Discard
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-1">
            <input
              type="text"
              className="flex-1 text-xs border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
              placeholder="e.g. Make it more urgent, shorter, funnier..."
              value={tweakInput}
              onChange={e => setTweakInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendTweak()}
              disabled={tweakLoading}
            />
            <button
              onClick={sendTweak}
              disabled={tweakLoading || !tweakInput.trim()}
              className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {tweakLoading ? '...' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingCard;
